import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Users,
    UserPlus,
    Trash2,
    Wallet,
    ChevronRight,
    ChevronLeft,
    Save,
    Loader2,
    Smartphone,
    Banknote,
    Landmark,
    ScrollText,
    Coins,
    HelpCircle,
    Plus,
    Pencil,
    Printer,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { QRCodeSVG } from 'qrcode.react';
import { PageHeader } from '../components/PageHeader';
import { MonthPicker, currentMonth, labelForMonth, useSharedMonth } from "../components/MonthPicker";
import { OpeningBalanceEditor } from '../components/OpeningBalanceEditor';
import { api, fmtDate, fmtDateTime, fmtMoney, buildUpiIntentUrl } from '../lib/api';

const METHOD_ICON = {
    upi: Smartphone,
    cash: Banknote,
    bank: Landmark,
    cheque: ScrollText,
    other: HelpCircle,
};
const TYPE_LABEL = { advance: 'Advance', salary_payment: 'Salary' };

function MethodBadge({ method }) {
    const Icon = METHOD_ICON[method] || HelpCircle;
    return (
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background/80 px-2 py-0.5 text-[11px] uppercase tracking-wide">
            <Icon size={11} /> {method || 'other'}
        </span>
    );
}

function TypeBadge({ type }) {
    const cls =
        type === 'advance'
            ? 'bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border-[hsl(var(--warning-border))]'
            : 'bg-[hsl(var(--price-decrease-bg))] text-[hsl(var(--price-decrease-fg))] border-[hsl(var(--price-decrease-border))]';
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${cls}`}>
            {type === 'advance' ? <Coins size={11} /> : <Wallet size={11} />}
            {TYPE_LABEL[type] || type}
        </span>
    );
}

/* ---------------------------------------------------------------------------
 * Add / Edit staff dialog
 * ------------------------------------------------------------------------- */
function StaffFormDialog({ open, onOpenChange, initial, onSaved }) {
    const [name, setName] = useState('');
    const [alias, setAlias] = useState('');
    const [monthlySalary, setMonthlySalary] = useState('');
    const [workingDays, setWorkingDays] = useState('30');
    const [upiId, setUpiId] = useState('');
    const [upiName, setUpiName] = useState('');
    const [phone, setPhone] = useState('');
    const [active, setActive] = useState(true);
    const [notes, setNotes] = useState('');
    // Sprint 2026-08 — perks
    const [paidLeaveAllowance, setPaidLeaveAllowance] = useState('0');
    const [zeroLeaveBonus, setZeroLeaveBonus] = useState('1000');
    const [saving, setSaving] = useState(false);
    const isEdit = !!initial?.id;

    useEffect(() => {
        if (open) {
            setName(initial?.name || '');
            setAlias(initial?.alias || '');
            setMonthlySalary(initial?.monthly_salary != null ? String(initial.monthly_salary) : '');
            setWorkingDays(initial?.working_days != null ? String(initial.working_days) : '30');
            setUpiId(initial?.upi_id || '');
            setUpiName(initial?.upi_name || '');
            setPhone(initial?.phone || '');
            setActive(initial?.active !== false);
            setNotes(initial?.notes || '');
            setPaidLeaveAllowance(
                initial?.paid_leave_allowance != null
                    ? String(initial.paid_leave_allowance)
                    : '0',
            );
            setZeroLeaveBonus(
                initial?.zero_leave_bonus_amount != null
                    ? String(initial.zero_leave_bonus_amount)
                    : '1000',
            );
        }
    }, [open, initial]);

    const canSubmit = useMemo(() => {
        if (saving) return false;
        if (!name.trim()) return false;
        const sal = Number(monthlySalary);
        // Empty input coerces to 0 — reject that too, otherwise users end
        // up with placeholder "₹0 salary" rows (which they mistake for
        // "add didn't work" and then create duplicates).
        if (Number.isNaN(sal) || sal <= 0) return false;
        const wd = Number(workingDays);
        if (Number.isNaN(wd) || wd <= 0) return false;
        return true;
    }, [name, monthlySalary, workingDays, saving]);

    async function handleSubmit() {
        setSaving(true);
        try {
            const body = {
                name: name.trim(),
                alias: alias.trim() || null,
                monthly_salary: Number(monthlySalary),
                working_days: Number(workingDays),
                paid_leave_allowance: Math.max(0, Math.min(31, parseInt(paidLeaveAllowance || '0', 10) || 0)),
                zero_leave_bonus_amount: Math.max(0, Number(zeroLeaveBonus) || 0),
                upi_id: upiId.trim() || null,
                upi_name: upiName.trim() || null,
                phone: phone.trim() || null,
                notes: notes.trim() || null,
            };
            let saved;
            if (isEdit) {
                saved = await api.updateStaff(initial.id, { ...body, active });
            } else {
                saved = await api.createStaff(body);
            }
            toast.success(isEdit ? 'Staff updated' : 'Staff added');
            onSaved?.(saved);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="staff-form-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit staff' : 'Add staff member'}</DialogTitle>
                    <DialogDescription>
                        Set the monthly salary base — attendance-based pro-rating happens each month.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="staff-name">Name</Label>
                        <Input
                            id="staff-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                            data-testid="staff-form-name"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="staff-alias" className="flex items-center gap-2">
                            Alias / Nickname
                            <span className="text-[10px] font-normal text-muted-foreground">
                                (optional — matches OCR nicknames like "culter" → Amarnath)
                            </span>
                        </Label>
                        <Input
                            id="staff-alias"
                            value={alias}
                            onChange={(e) => setAlias(e.target.value)}
                            placeholder="e.g. culter"
                            data-testid="staff-form-alias"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="staff-salary">Monthly salary (₹)</Label>
                            <Input
                                id="staff-salary"
                                type="number"
                                step="0.01"
                                min="0"
                                inputMode="decimal"
                                value={monthlySalary}
                                onChange={(e) => setMonthlySalary(e.target.value)}
                                data-testid="staff-form-salary"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="staff-wd">Working days / month</Label>
                            <Input
                                id="staff-wd"
                                type="number"
                                step="1"
                                min="1"
                                max="31"
                                value={workingDays}
                                onChange={(e) => setWorkingDays(e.target.value)}
                                data-testid="staff-form-workingdays"
                            />
                        </div>
                    </div>
                    {/* Sprint 2026-08 — perks & benefits */}
                    <div className="rounded-md border border-dashed border-primary/40 bg-primary/5 p-3 space-y-2">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                            Perks & benefits
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="staff-pla" className="text-xs">Paid leaves / month</Label>
                                <Input
                                    id="staff-pla"
                                    type="number"
                                    step="1"
                                    min="0"
                                    max="31"
                                    value={paidLeaveAllowance}
                                    onChange={(e) => setPaidLeaveAllowance(e.target.value)}
                                    placeholder="0"
                                    data-testid="staff-form-paid-leaves"
                                />
                                <div className="text-[10px] text-muted-foreground leading-tight">
                                    Leaves paid as if worked. Old/loyal staff: 2, new joiners: 0.
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="staff-zlb" className="text-xs">Perfect-attendance bonus (₹)</Label>
                                <Input
                                    id="staff-zlb"
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={zeroLeaveBonus}
                                    onChange={(e) => setZeroLeaveBonus(e.target.value)}
                                    placeholder="1000"
                                    data-testid="staff-form-zero-leave-bonus"
                                />
                                <div className="text-[10px] text-muted-foreground leading-tight">
                                    Extra ₹ paid when they take 0 leaves in a month. 0 to disable.
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="staff-upi">UPI ID (optional)</Label>
                            <Input
                                id="staff-upi"
                                placeholder="staff@upi"
                                value={upiId}
                                onChange={(e) => setUpiId(e.target.value)}
                                data-testid="staff-form-upi"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="staff-upi-name">Payee name for UPI</Label>
                            <Input
                                id="staff-upi-name"
                                placeholder={name || 'Payee'}
                                value={upiName}
                                onChange={(e) => setUpiName(e.target.value)}
                                data-testid="staff-form-upi-name"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="staff-phone">Phone (optional)</Label>
                            <Input
                                id="staff-phone"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                data-testid="staff-form-phone"
                            />
                        </div>
                        {isEdit && (
                            <div className="flex items-end gap-2">
                                <div className="flex items-center gap-2 pb-1">
                                    <Switch checked={active} onCheckedChange={setActive} data-testid="staff-form-active" />
                                    <Label>Active</Label>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="staff-notes">Notes (optional)</Label>
                        <Input
                            id="staff-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="staff-form-notes"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="staff-form-cancel">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit} data-testid="staff-form-submit">
                        {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : (isEdit ? 'Save changes' : 'Add staff')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ---------------------------------------------------------------------------
 * Pay Salary dialog — one-tap UPI payout flow (Sprint 2026-08).
 *
 * Owner spec: "How to pay salary to staff from app" — instead of the generic
 * "Record transaction" dialog which asks type/method/amount/month, this is a
 * dedicated 3-step flow:
 *   1. Confirm amount (pre-filled = salary_due). If the staff has no saved
 *      UPI ID, prompt to add it inline (persists to the staff record).
 *   2. Open UPI intent (upi://pay?pa=…&pn=…&am=…) — user pays inside their
 *      preferred UPI app (GPay / PhonePe / Paytm / BHIM).
 *   3. On return, "Did it succeed?" — Yes logs a `salary_payment` transaction
 *      (with optional UTR pasted from their UPI app's success screen);
 *      No cancels cleanly, nothing recorded.
 *
 * PWA limitation: web browsers can't auto-read the UPI app's return status.
 * When we ship the TWA/native Android build later, we'll switch step 3 to
 * consume the OS activity result directly — no code change on this button.
 *
 * Fallback: if the staff has no UPI *and* the owner doesn't want to add one
 * right now, a "Mark paid in cash" secondary action records the same txn
 * with method='cash' — same ledger effect, just no UPI intent involved.
 * ------------------------------------------------------------------------- */
function PaySalaryDialog({ open, onOpenChange, staff, month, salaryDue, onSaved }) {
    // Three logical phases. `amount` phase is the entry point; `confirm` runs
    // after the user has (presumably) completed the UPI transfer.
    const [phase, setPhase] = useState('amount');   // 'amount' | 'confirm'
    const [amount, setAmount] = useState('');
    const [upiId, setUpiId] = useState('');
    const [saveUpi, setSaveUpi] = useState(true);   // persist UPI to staff record?
    const [utr, setUtr] = useState('');
    const [saving, setSaving] = useState(false);
    const [upiOpened, setUpiOpened] = useState(false);

    // Reset every time the dialog opens so previous state doesn't leak between
    // different staff.
    useEffect(() => {
        if (!open) return;
        setPhase('amount');
        setAmount(salaryDue > 0 ? String(Math.round(salaryDue)) : '');
        setUpiId(staff?.upi_id || '');
        setSaveUpi(!staff?.upi_id);
        setUtr('');
        setUpiOpened(false);
    }, [open, staff, salaryDue]);

    const amountNum = Number(amount);
    const amountValid = amount !== '' && Number.isFinite(amountNum) && amountNum > 0;
    const hasUpi = (upiId || '').trim().length > 0;
    // Basic sanity — a UPI VPA looks like `name@handle`. We don't enforce
    // strictly (some banks use `phone@handle`), just fail obvious garbage.
    const upiLooksValid = /^[a-z0-9._-]{2,}@[a-z][a-z0-9.-]{1,}$/i.test((upiId || '').trim());

    const upiUrl = useMemo(() => {
        if (!hasUpi || !amountValid) return null;
        return buildUpiIntentUrl({
            pa: upiId.trim(),
            pn: staff?.upi_name || staff?.name || 'Staff',
            am: amountNum,
            tn: `Salary · ${staff?.name} · ${month}`,
        });
    }, [hasUpi, upiId, amountValid, amountNum, staff, month]);

    // Step 1 → Step 2: open the UPI intent link.
    async function handleOpenUpi() {
        if (!upiLooksValid) {
            toast.error('That UPI ID looks off. It should be like "name@bank".');
            return;
        }
        // If the user added/edited a UPI here, persist it to the staff
        // record so next time we don't ask again.
        const currentUpi = (upiId || '').trim();
        if (saveUpi && currentUpi && currentUpi !== staff?.upi_id) {
            try {
                await api.updateStaff(staff.id, { upi_id: currentUpi });
                toast.success('UPI saved to staff record.');
            } catch {
                toast.error('Could not save UPI, but you can still proceed.');
            }
        }
        // Launch the UPI intent. On mobile browsers this pops the OS UPI-app
        // chooser (GPay/PhonePe/Paytm/BHIM). On desktop nothing usually
        // happens — we still show the confirm step in case the user paid
        // through some other channel.
        setUpiOpened(true);
        try {
            // Use assignment (not window.open) so mobile browsers treat it
            // as a first-party navigation — Chrome mobile blocks
            // window.open('upi://...') as popup.
            window.location.href = upiUrl;
        } catch {
            /* no-op — user will still see the confirm screen */
        }
        // Small delay lets the OS take over before we swap phases, so the
        // confirm screen doesn't flash under the UPI app.
        setTimeout(() => setPhase('confirm'), 600);
    }

    // Step 2 → done: log the salary_payment transaction.
    async function handleMarkPaid(method /* 'upi' | 'cash' */) {
        if (!amountValid) return;
        setSaving(true);
        try {
            const saved = await api.createStaffTransaction(staff.id, {
                type: 'salary_payment',
                amount: amountNum,
                method,
                reference: (utr || '').trim() || null,
                note: `Salary payout for ${month}`,
                month,
            });
            toast.success(`Salary payment ${fmtMoney(amountNum)} recorded.`);
            onSaved?.(saved);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to record payment.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => (!saving && onOpenChange(v))}>
            <DialogContent className="max-w-md" data-testid="pay-salary-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Wallet size={18} className="text-primary" />
                        Pay salary
                    </DialogTitle>
                    <DialogDescription>
                        To <span className="font-medium text-foreground">{staff?.name}</span>
                        {' · '}For <span className="font-mono">{month}</span>
                    </DialogDescription>
                </DialogHeader>

                {phase === 'amount' && (
                    <div className="space-y-4">
                        <div className="rounded-md bg-muted/40 border border-border/60 p-3 space-y-1">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                Salary due right now
                            </div>
                            <div className="text-2xl font-heading font-semibold tabular-nums">
                                {fmtMoney(salaryDue || 0)}
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="pay-salary-amount">Amount to pay (₹)</Label>
                            <Input
                                id="pay-salary-amount"
                                type="number"
                                inputMode="decimal"
                                min="1"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder={String(Math.round(salaryDue || 0))}
                                className="text-lg font-mono tabular-nums"
                                autoFocus
                                data-testid="pay-salary-amount-input"
                            />
                            <div className="text-[11px] text-muted-foreground">
                                Edit if you're paying partial. This gets logged as a
                                salary payment for {month}.
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="pay-salary-upi" className="flex items-center gap-2">
                                Staff UPI ID
                                {!staff?.upi_id && (
                                    <span className="text-[10px] font-normal uppercase tracking-wider text-amber-600 dark:text-amber-400">
                                        not saved yet
                                    </span>
                                )}
                            </Label>
                            <Input
                                id="pay-salary-upi"
                                value={upiId}
                                onChange={(e) => setUpiId(e.target.value.toLowerCase())}
                                placeholder="name@upi (e.g. amar@ybl)"
                                className="font-mono text-sm"
                                data-testid="pay-salary-upi-input"
                            />
                            {upiId && !upiLooksValid && (
                                <div className="text-[11px] text-destructive">
                                    Doesn't look like a valid UPI ID. Expected format: name@bank
                                </div>
                            )}
                            {!staff?.upi_id && upiLooksValid && (
                                <label className="flex items-center gap-2 text-[11px] text-muted-foreground pt-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={saveUpi}
                                        onChange={(e) => setSaveUpi(e.target.checked)}
                                        className="rounded border-border"
                                        data-testid="pay-salary-save-upi"
                                    />
                                    Save this UPI to {staff?.name}'s profile for next time
                                </label>
                            )}
                        </div>

                        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-1">
                            <Button
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                data-testid="pay-salary-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setPhase('confirm')}
                                disabled={!amountValid}
                                data-testid="pay-salary-cash-btn"
                                title="Skip UPI — record a cash payout"
                            >
                                <Banknote size={14} className="mr-1.5" />
                                Paying in cash
                            </Button>
                            <Button
                                onClick={handleOpenUpi}
                                disabled={!amountValid || !upiLooksValid}
                                data-testid="pay-salary-open-upi-btn"
                            >
                                <Smartphone size={14} className="mr-1.5" />
                                Open UPI app
                            </Button>
                        </DialogFooter>
                    </div>
                )}

                {phase === 'confirm' && (
                    <div className="space-y-4">
                        <div className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-1">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                {upiOpened ? 'UPI app was opened for' : 'Cash payout of'}
                            </div>
                            <div className="text-2xl font-heading font-semibold tabular-nums">
                                {fmtMoney(amountNum)}
                            </div>
                            {upiOpened && (
                                <div className="text-[11px] text-muted-foreground pt-0.5">
                                    Complete the payment in GPay / PhonePe / your UPI app,
                                    then confirm below.
                                </div>
                            )}
                        </div>

                        {upiOpened && (
                            <div className="space-y-1.5">
                                <Label htmlFor="pay-salary-utr" className="flex items-center gap-2">
                                    UTR / Transaction ID
                                    <span className="text-[10px] font-normal text-muted-foreground">
                                        (optional — 12 digits, from your UPI app's success screen)
                                    </span>
                                </Label>
                                <Input
                                    id="pay-salary-utr"
                                    value={utr}
                                    onChange={(e) => setUtr(e.target.value)}
                                    placeholder="e.g. 412345678901"
                                    className="font-mono text-sm"
                                    data-testid="pay-salary-utr-input"
                                />
                            </div>
                        )}

                        <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 text-[12px] text-amber-900 dark:text-amber-100">
                            <strong>Only tap "Yes, mark paid"</strong> if the money actually
                            left your account. This creates a permanent salary payment record.
                        </div>

                        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-1">
                            <Button
                                variant="ghost"
                                onClick={() => setPhase('amount')}
                                disabled={saving}
                                data-testid="pay-salary-back-btn"
                            >
                                <ChevronLeft size={14} className="mr-1" /> Back
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={saving}
                                data-testid="pay-salary-confirm-no"
                            >
                                No, cancel
                            </Button>
                            <Button
                                onClick={() => handleMarkPaid(upiOpened ? 'upi' : 'cash')}
                                disabled={saving}
                                data-testid="pay-salary-confirm-yes"
                            >
                                {saving ? (
                                    <><Loader2 size={14} className="mr-1.5 animate-spin" /> Recording…</>
                                ) : (
                                    <>Yes, mark paid</>
                                )}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}


/* ---------------------------------------------------------------------------
 * Record staff transaction dialog (advance or salary payment)
 * ------------------------------------------------------------------------- */
function RecordStaffTxnDialog({ open, onOpenChange, staff, month, outstanding, onSaved }) {
    const [amount, setAmount] = useState('');
    const [type, setType] = useState('salary_payment');
    const [method, setMethod] = useState('upi');
    const [reference, setReference] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setAmount('');
            setType('salary_payment');
            setMethod('upi');
            setReference('');
            setNote('');
        }
    }, [open]);

    const amountNum = Number(amount);
    const amountValid = amount !== '' && !Number.isNaN(amountNum) && amountNum > 0;

    const upiUrl = useMemo(() => {
        if (method !== 'upi') return null;
        if (!staff?.upi_id || !amountValid) return null;
        return buildUpiIntentUrl({
            pa: staff.upi_id,
            pn: staff.upi_name || staff.name,
            am: amountNum,
            tn: `${type === 'advance' ? 'Advance' : 'Salary'} · ${staff.name}`,
        });
    }, [method, staff, amountNum, amountValid, type]);

    async function handleSubmit() {
        if (!amountValid || !staff?.id) return;
        setSaving(true);
        try {
            const saved = await api.createStaffTransaction(staff.id, {
                type,
                amount: amountNum,
                method,
                reference: reference.trim() || null,
                note: note.trim() || null,
                month,
            });
            toast.success(`${TYPE_LABEL[type]} of ${fmtMoney(amountNum)} recorded`);
            onSaved?.(saved);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to record');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="staff-txn-dialog">
                <DialogHeader>
                    <DialogTitle>Record transaction</DialogTitle>
                    <DialogDescription>
                        For <span className="font-medium text-foreground">{staff?.name}</span>
                        {' · '}Month <span className="font-mono">{month}</span>
                        {outstanding !== undefined && outstanding > 0 && (
                            <>{' · '}Outstanding <span className="font-mono">{fmtMoney(outstanding)}</span></>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label>Transaction type</Label>
                        <RadioGroup value={type} onValueChange={setType} className="grid grid-cols-2 gap-2">
                            <label
                                htmlFor="stxn-type-salary"
                                className={`cursor-pointer flex items-start gap-2 rounded-md border p-3 text-sm transition-colors ${type === 'salary_payment' ? 'border-primary bg-accent' : 'border-border hover:bg-muted/40'}`}
                            >
                                <RadioGroupItem
                                    value="salary_payment"
                                    id="stxn-type-salary"
                                    data-testid="staff-txn-type-salary"
                                />
                                <div>
                                    <div className="font-medium flex items-center gap-1"><Wallet size={12} /> Salary payment</div>
                                    <div className="text-[11px] text-muted-foreground">Pay towards this month's salary.</div>
                                </div>
                            </label>
                            <label
                                htmlFor="stxn-type-advance"
                                className={`cursor-pointer flex items-start gap-2 rounded-md border p-3 text-sm transition-colors ${type === 'advance' ? 'border-primary bg-accent' : 'border-border hover:bg-muted/40'}`}
                            >
                                <RadioGroupItem
                                    value="advance"
                                    id="stxn-type-advance"
                                    data-testid="staff-txn-type-advance"
                                />
                                <div>
                                    <div className="font-medium flex items-center gap-1"><Coins size={12} /> Advance</div>
                                    <div className="text-[11px] text-muted-foreground">Money given before salary is due.</div>
                                </div>
                            </label>
                        </RadioGroup>
                    </div>

                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="stxn-amount">Amount (₹)</Label>
                            {outstanding !== undefined && outstanding > 0 && (
                                <button
                                    type="button"
                                    className="text-[11px] text-primary hover:underline"
                                    onClick={() => setAmount(String(outstanding))}
                                    data-testid="staff-txn-set-outstanding"
                                >
                                    Set to outstanding
                                </button>
                            )}
                        </div>
                        <Input
                            id="stxn-amount"
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            autoFocus
                            data-testid="staff-txn-amount"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label>Method</Label>
                        <RadioGroup value={method} onValueChange={setMethod} className="grid grid-cols-2 gap-2">
                            {[
                                { v: 'upi', label: 'UPI', icon: Smartphone },
                                { v: 'cash', label: 'Cash', icon: Banknote },
                                { v: 'bank', label: 'Bank', icon: Landmark },
                                { v: 'cheque', label: 'Cheque', icon: ScrollText },
                            ].map(({ v, label, icon: Icon }) => {
                                const active = method === v;
                                return (
                                    <label
                                        key={v}
                                        htmlFor={`stxn-method-${v}`}
                                        className={`cursor-pointer flex items-center gap-2 rounded-md border p-2.5 text-sm transition-colors ${active ? 'border-primary bg-accent' : 'border-border hover:bg-muted/40'}`}
                                    >
                                        <RadioGroupItem value={v} id={`stxn-method-${v}`} data-testid={`staff-txn-method-${v}`} />
                                        <Icon size={13} />
                                        <span className="font-medium">{label}</span>
                                    </label>
                                );
                            })}
                        </RadioGroup>
                    </div>

                    {method === 'upi' && (
                        <div className="rounded-md border border-border bg-muted/30 p-3">
                            {!staff?.upi_id && (
                                <div className="text-xs text-muted-foreground">
                                    No UPI ID saved for this staff member. Edit staff to add one, or pick another method.
                                </div>
                            )}
                            {upiUrl && (
                                <div className="flex items-center gap-3">
                                    <div className="rounded-md bg-white p-2 border border-border shrink-0">
                                        <QRCodeSVG value={upiUrl} size={112} level="M" data-testid="staff-txn-upi-qr" />
                                    </div>
                                    <div className="flex-1 min-w-0 text-xs text-muted-foreground space-y-1">
                                        <div>Scan the QR from any UPI app (GPay/PhonePe/Paytm/BHIM) to pay
                                            <span className="font-mono text-foreground"> {staff.upi_id}</span>.</div>
                                        <a
                                            href={upiUrl}
                                            className="inline-block text-primary hover:underline"
                                            target="_blank" rel="noreferrer"
                                            data-testid="staff-txn-upi-link"
                                        >
                                            Open UPI app
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="stxn-ref">Reference (optional)</Label>
                            <Input
                                id="stxn-ref"
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                placeholder={method === 'upi' ? 'UPI txn id' : method === 'cheque' ? 'Cheque no.' : 'Ref'}
                                data-testid="staff-txn-reference"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="stxn-note">Note (optional)</Label>
                            <Input
                                id="stxn-note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                data-testid="staff-txn-note"
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="staff-txn-cancel">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!amountValid || saving} data-testid="staff-txn-submit">
                        {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : `Record ${amountValid ? fmtMoney(amountNum) : ''}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ---------------------------------------------------------------------------
 * Staff detail panel (attendance + transactions)
 * ------------------------------------------------------------------------- */
function StaffDetailPanel({ staff, month, onChange, onBack }) {
    const [monthDoc, setMonthDoc] = useState(null);
    const [txns, setTxns] = useState(null);
    const [summary, setSummary] = useState(null);
    const [daysInput, setDaysInput] = useState('');
    const [wdInput, setWdInput] = useState('');
    // Sprint 2026-08: per-date leave tracking. When user adds/removes
    // a leave chip, `daysInput` auto-updates to (days_in_month - leaves.length).
    const [leaveDates, setLeaveDates] = useState([]);
    const [newLeaveDate, setNewLeaveDate] = useState('');
    const [savingAtt, setSavingAtt] = useState(false);
    const [txnOpen, setTxnOpen] = useState(false);
    const [payOpen, setPayOpen] = useState(false);  // Sprint 2026-08: dedicated Pay Salary flow
    const [editOpen, setEditOpen] = useState(false);
    const [confirmDelTxn, setConfirmDelTxn] = useState(null);
    const [confirmDelStaff, setConfirmDelStaff] = useState(false);
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const [m, t, s] = await Promise.all([
                api.getStaffMonth(staff.id, month),
                api.listStaffTransactions(staff.id, { month }),
                api.getStaffSummary(staff.id, { month }),
            ]);
            setMonthDoc(m);
            setTxns(t || []);
            setSummary(s);
            setDaysInput(String(m?.days_worked ?? 0));
            setWdInput(String(m?.working_days ?? staff?.working_days ?? 30));
            setLeaveDates(Array.isArray(m?.leave_dates) ? [...m.leave_dates].sort() : []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load staff detail');
        }
    }

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [staff?.id, month]);

    // Sprint 2026-08: allow mobile-card "Pay" chip to deep-link straight
    // into the pay-salary dialog when the drawer opens for the matching
    // staff. The chip sets `window.location.hash = 'pay-<staffId>'`; here we
    // read it once the drawer has mounted for this staff, pop the dialog,
    // and clear the hash so subsequent refreshes don't re-trigger.
    useEffect(() => {
        if (!staff?.id) return;
        const hash = (typeof window !== 'undefined' ? window.location.hash : '') || '';
        if (hash === `#pay-${staff.id}`) {
            setPayOpen(true);
            try {
                // Wipe the hash without a scroll jump.
                history.replaceState(null, '', window.location.pathname + window.location.search);
            } catch { /* noop */ }
        }
    }, [staff?.id]);

    // Sprint 2026-08: unified feed of transactions + leaves (newest first)
    // powering the "Activity" table below.
    const activityFeed = useMemo(() => {
        const items = [];
        for (const t of (txns || [])) items.push({ kind: 'txn', at: t.paid_at, txn: t });
        for (const d of (leaveDates || [])) items.push({ kind: 'leave', at: d + 'T12:00:00Z', date: d });
        items.sort((a, b) => (b.at || '').localeCompare(a.at || ''));
        return items;
    }, [txns, leaveDates]);

    async function saveAttendance() {
        setSavingAtt(true);
        try {
            await api.updateStaffMonth(staff.id, month, {
                // Send leave_dates as the source of truth when populated;
                // backend derives days_worked from it automatically.
                leave_dates: leaveDates,
                days_worked: leaveDates.length > 0 ? undefined : Number(daysInput || 0),
                working_days: Number(wdInput || staff.working_days || 30),
            });
            toast.success('Attendance saved');
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save');
        } finally {
            setSavingAtt(false);
        }
    }

    async function deleteTxn() {
        if (!confirmDelTxn) return;
        setBusy(true);
        try {
            await api.deleteStaffTransaction(confirmDelTxn.id);
            toast.success('Transaction removed');
            setConfirmDelTxn(null);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to delete');
        } finally {
            setBusy(false);
        }
    }

    async function deleteStaff() {
        setBusy(true);
        try {
            await api.deleteStaff(staff.id);
            toast.success(`${staff.name} removed`);
            setConfirmDelStaff(false);
            onChange?.();
            onBack?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to delete');
        } finally {
            setBusy(false);
        }
    }

    const outstandingTone =
        (summary?.outstanding || 0) > 0 ? 'negative' :
            (summary?.outstanding || 0) < 0 ? 'positive' : 'default';
    const toneCls =
        outstandingTone === 'negative' ? 'text-[hsl(var(--price-increase-fg))]' :
            outstandingTone === 'positive' ? 'text-[hsl(var(--price-decrease-fg))]' : '';

    return (
        <Card className="p-5 border-border/80" data-testid="staff-detail-panel">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="gap-1" onClick={onBack} data-testid="staff-detail-back">
                        <ChevronLeft size={14} /> All staff
                    </Button>
                    <div>
                        <div className="font-heading text-xl leading-none uppercase tracking-wide">{staff.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Monthly base ₹{Number(staff.monthly_salary).toLocaleString('en-IN')}
                            {staff.upi_id && <> · UPI <span className="font-mono">{staff.upi_id}</span></>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        data-testid="staff-detail-payslip"
                    >
                        <Link to={`/staff/${staff.id}/payslip?month=${encodeURIComponent(month)}`}>
                            <Printer size={13} /> Payslip
                        </Link>
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1" onClick={() => setEditOpen(true)} data-testid="staff-detail-edit">
                        <Pencil size={13} /> Edit
                    </Button>
                    <Button
                        variant="ghost" size="sm"
                        className="gap-1 text-[hsl(var(--price-increase-fg))]"
                        onClick={() => setConfirmDelStaff(true)}
                        data-testid="staff-detail-delete"
                    >
                        <Trash2 size={13} /> Remove
                    </Button>
                </div>
            </div>

            {/* Balance stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <BalanceStat
                    label="Salary due"
                    value={fmtMoney(summary?.salary_due || 0)}
                    testid="staff-stat-salary-due"
                />
                <BalanceStat
                    label="Advances taken"
                    value={fmtMoney(summary?.advances_taken || 0)}
                    testid="staff-stat-advances"
                />
                <BalanceStat
                    label="Salary paid"
                    value={fmtMoney(summary?.salary_paid || 0)}
                    testid="staff-stat-paid"
                />
                <BalanceStat
                    label="Outstanding"
                    value={
                        (summary?.outstanding || 0) === 0 ? 'Settled'
                            : (summary?.outstanding || 0) > 0
                                ? `You owe ${fmtMoney(summary.outstanding)}`
                                : `Advance ${fmtMoney(Math.abs(summary?.outstanding || 0))}`
                    }
                    className={toneCls}
                    testid="staff-stat-outstanding"
                />
            </div>

            <div className="mb-5">
                <OpeningBalanceEditor
                    entityLabel="Staff"
                    value={{
                        opening_balance_amount: staff?.opening_balance_amount,
                        opening_balance_direction: staff?.opening_balance_direction,
                        opening_balance_as_of: staff?.opening_balance_as_of,
                    }}
                    directionOptions={[
                        {
                            value: 'we_owe',
                            label: 'We owe them (unpaid salary carryover)',
                            shortLabel: 'We owe them',
                            hint: 'Salary from before you started tracking here.',
                        },
                        {
                            value: 'staff_owes',
                            label: 'They owe us (advance previously given)',
                            shortLabel: 'They owe us',
                            hint: 'Advance you paid before adopting Ease My Bill.',
                        },
                    ]}
                    onSave={async (patch) => {
                        await api.updateStaff(staff.id, patch);
                        onChange?.();
                    }}
                    testid="staff-opening-balance"
                />
            </div>

            {/* Salary breakdown (Sprint 2026-08) — transparent view of how
                the current month's salary_due is composed, so users can
                see paid-leave allowance, unpaid deductions, and the
                perfect-attendance bonus. */}
            {summary?.days_in_month != null && Number(staff.monthly_salary || 0) > 0 && (
                <div className="rounded-md border border-border p-4 mb-5" data-testid="staff-salary-breakdown">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                            Salary breakdown · {labelForMonth(month)}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            {summary.days_in_month}-day month · per day {fmtMoney(summary.per_day_salary || 0)}
                        </div>
                    </div>
                    <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                            <span>
                                Base ({(summary.days_worked || 0)} worked + {(summary.paid_leaves_used || 0)} paid leaves)
                            </span>
                            <span className="tabular-nums font-mono">{fmtMoney(summary.base_earned || 0)}</span>
                        </div>
                        {Number(summary.unpaid_leaves || 0) > 0 && (
                            <div className="flex justify-between text-muted-foreground">
                                <span>Unpaid leaves ({summary.unpaid_leaves}) — already excluded from base</span>
                                <span className="tabular-nums font-mono">—</span>
                            </div>
                        )}
                        {Number(summary.zero_leave_bonus || 0) > 0 && (
                            <div className="flex justify-between text-[hsl(var(--price-decrease-fg))]">
                                <span>Perfect-attendance bonus (0 leaves)</span>
                                <span className="tabular-nums font-mono">+ {fmtMoney(summary.zero_leave_bonus)}</span>
                            </div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-border font-semibold">
                            <span>Salary due</span>
                            <span className="tabular-nums font-mono">{fmtMoney(summary.salary_due || 0)}</span>
                        </div>
                    </div>
                    {Number(staff.paid_leave_allowance || 0) > 0 && (
                        <div className="text-[11px] text-muted-foreground mt-3">
                            Allowance: {staff.paid_leave_allowance} paid leave{staff.paid_leave_allowance > 1 ? 's' : ''} per month
                            {Number(summary.unused_leaves || 0) > 0 && (
                                <span> · <span className="text-foreground">{summary.unused_leaves} unused</span> this month</span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Attendance */}
            <div className="rounded-md border border-border p-4 mb-5" data-testid="staff-attendance-card">
                <div className="flex items-center gap-2 mb-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Attendance · {labelForMonth(month)}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                    <div className="space-y-1.5">
                        <Label htmlFor="att-days">Days worked</Label>
                        <Input
                            id="att-days"
                            type="number"
                            step="0.5"
                            min="0"
                            max="31"
                            value={daysInput}
                            onChange={(e) => setDaysInput(e.target.value)}
                            data-testid="staff-attendance-days"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="att-wd">Working days / month</Label>
                        <Input
                            id="att-wd"
                            type="number"
                            step="1"
                            min="1"
                            max="31"
                            value={wdInput}
                            onChange={(e) => setWdInput(e.target.value)}
                            data-testid="staff-attendance-workingdays"
                        />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                            Computed salary
                            {(() => {
                                const days = Number(daysInput || 0);
                                const wd = Number(wdInput || staff.working_days || 30);
                                const savedDays = Number(monthDoc?.days_worked || 0);
                                const savedWd = Number(monthDoc?.working_days || staff.working_days || 30);
                                const dirty = days !== savedDays || wd !== savedWd;
                                if (!dirty) return null;
                                return (
                                    <span
                                        className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium normal-case tracking-normal px-1.5 py-0.5 rounded-full bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border border-[hsl(var(--warning-border))]"
                                        data-testid="staff-attendance-unsaved-badge"
                                    >
                                        Preview \u2014 not saved
                                    </span>
                                );
                            })()}
                        </div>
                        <div className="font-mono text-2xl mt-0.5 tabular-nums" data-testid="staff-attendance-computed">
                            {(() => {
                                const days = Number(daysInput || 0);
                                const wd = Math.max(1, Number(wdInput || staff.working_days || 30));
                                const base = Number(staff.monthly_salary || 0);
                                // Live preview: pro-rate the base, capped at 100% (never over-pay for overtime days).
                                const ratio = Math.min(days / wd, 1);
                                const preview = base * ratio;
                                return fmtMoney(Number.isFinite(preview) ? preview : 0);
                            })()}
                        </div>
                    </div>
                    <div>
                        <Button
                            onClick={saveAttendance}
                            disabled={savingAtt}
                            className="gap-2 w-full"
                            data-testid="staff-attendance-save"
                        >
                            {savingAtt ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            {savingAtt ? 'Saving…' : 'Save attendance'}
                        </Button>
                    </div>
                </div>
                {/* Sprint 2026-08 quick-preset: full-attendance in one tap so
                    the user doesn't have to type "31" (or 30/29) every payday.
                    Uses the actual calendar days-in-month from summary. */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        Quick fill:
                    </span>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            const dim = Number(summary?.days_in_month || wdInput || 30);
                            setDaysInput(String(dim));
                            setWdInput(String(dim));
                        }}
                        data-testid="staff-attendance-mark-all-present"
                    >
                        Mark all present ({Number(summary?.days_in_month || 0)} days)
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                            const dim = Number(summary?.days_in_month || wdInput || 30);
                            setDaysInput(String(Math.max(0, dim - 1)));
                            setWdInput(String(dim));
                        }}
                        data-testid="staff-attendance-one-leave"
                    >
                        1 leave
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                            const dim = Number(summary?.days_in_month || wdInput || 30);
                            setDaysInput(String(Math.max(0, dim - 2)));
                            setWdInput(String(dim));
                        }}
                        data-testid="staff-attendance-two-leave"
                    >
                        2 leaves
                    </Button>
                </div>
                {/* Sprint 2026-08: per-date leave tracking. */}
                <div className="mt-4 pt-4 border-t border-border/80" data-testid="staff-leave-dates-block">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                            Leaves this month
                            {leaveDates.length > 0 && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-mono">
                                    {leaveDates.length}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        {leaveDates.length === 0 && (
                            <div className="text-[11px] text-muted-foreground italic">
                                No leaves recorded. Add a date below when they take a leave, or use the quick-fill buttons above.
                            </div>
                        )}
                        {leaveDates.map((d) => (
                            <span
                                key={d}
                                className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-muted/50 hover:bg-destructive/10 hover:border-destructive/50 px-2 py-0.5 text-[11px] font-mono"
                                data-testid={`staff-leave-chip-${d}`}
                            >
                                {fmtDate(d + 'T00:00:00Z')}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLeaveDates((xs) => xs.filter((x) => x !== d));
                                        setDaysInput((prev) => {
                                            const dim = Number(summary?.days_in_month || wdInput || 30);
                                            return String(Math.max(0, dim - (leaveDates.length - 1)));
                                        });
                                    }}
                                    className="hover:text-destructive"
                                    data-testid={`staff-leave-remove-${d}`}
                                    aria-label={`Remove leave on ${d}`}
                                >
                                    <Trash2 size={11} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            type="date"
                            value={newLeaveDate}
                            onChange={(e) => setNewLeaveDate(e.target.value)}
                            min={`${month}-01`}
                            max={`${month}-${String(summary?.days_in_month || 31).padStart(2, '0')}`}
                            className="h-9 max-w-[180px]"
                            data-testid="staff-leave-date-input"
                        />
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!newLeaveDate || !newLeaveDate.startsWith(month + '-')}
                            onClick={() => {
                                if (!newLeaveDate) return;
                                if (!newLeaveDate.startsWith(month + '-')) {
                                    toast.error(`Date must fall in ${labelForMonth(month)}`);
                                    return;
                                }
                                if (leaveDates.includes(newLeaveDate)) {
                                    toast.info('That date is already marked as leave');
                                    return;
                                }
                                const next = [...leaveDates, newLeaveDate].sort();
                                setLeaveDates(next);
                                const dim = Number(summary?.days_in_month || wdInput || 30);
                                setDaysInput(String(Math.max(0, dim - next.length)));
                                setNewLeaveDate('');
                            }}
                            data-testid="staff-leave-add-btn"
                        >
                            <Plus size={13} className="mr-1" /> Add leave
                        </Button>
                        <div className="text-[11px] text-muted-foreground">
                            Or use quick-fill above
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity — unified timeline: advances, salary payments, AND leaves.
                Sprint 2026-08 (user request): "you are showing the dates when
                advances are taken under staff name then also show their leaves". */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Activity · {labelForMonth(month)}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        className="gap-1"
                        onClick={() => setPayOpen(true)}
                        data-testid="staff-pay-salary-btn"
                        title="Pay salary via UPI or record a cash payout"
                    >
                        <Wallet size={14} /> Pay salary
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() => setTxnOpen(true)}
                        data-testid="staff-record-txn-btn"
                    >
                        <Plus size={14} /> Record transaction
                    </Button>
                </div>
            </div>
            {txns === null ? (
                <div className="space-y-2">
                    {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                </div>
            ) : (txns.length === 0 && leaveDates.length === 0) ? (
                <div className="p-6 rounded-md border border-dashed border-border text-sm text-muted-foreground text-center">
                    No advances, salary payments, or leaves recorded for this month yet.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm" data-testid="staff-activity-table">
                        <thead className="bg-muted/40 text-muted-foreground">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Date</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-right px-3 py-2 font-medium">Amount</th>
                                <th className="text-left px-3 py-2 font-medium">Method</th>
                                <th className="text-left px-3 py-2 font-medium">Ref / Note</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {activityFeed.map((it) => (
                                it.kind === 'leave' ? (
                                    <tr
                                        key={`leave-${it.date}`}
                                        className="border-t border-border hover:bg-muted/40 bg-amber-50/30 dark:bg-amber-950/10"
                                        data-testid={`staff-leave-row-${it.date}`}
                                    >
                                        <td className="px-3 py-2 text-muted-foreground">{fmtDate(it.at)}</td>
                                        <td className="px-3 py-2">
                                            <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-[11px] font-medium">
                                                Leave
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right text-muted-foreground italic">—</td>
                                        <td className="px-3 py-2 text-muted-foreground italic">—</td>
                                        <td className="px-3 py-2 text-muted-foreground text-xs italic">
                                            Absent this day
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => {
                                                    setLeaveDates((xs) => xs.filter((x) => x !== it.date));
                                                    const dim = Number(summary?.days_in_month || wdInput || 30);
                                                    setDaysInput(String(Math.max(0, dim - (leaveDates.length - 1))));
                                                }}
                                                aria-label="Remove leave"
                                                data-testid={`staff-leave-row-remove-${it.date}`}
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr
                                        key={it.txn.id}
                                        className="border-t border-border hover:bg-muted/40"
                                        data-testid={`staff-txn-row-${it.txn.id}`}
                                    >
                                        <td className="px-3 py-2 text-muted-foreground">{fmtDateTime(it.txn.paid_at)}</td>
                                        <td className="px-3 py-2"><TypeBadge type={it.txn.type} /></td>
                                        <td className="px-3 py-2 text-right tabular-nums font-mono font-medium">{fmtMoney(it.txn.amount)}</td>
                                        <td className="px-3 py-2"><MethodBadge method={it.txn.method} /></td>
                                        <td className="px-3 py-2 text-muted-foreground text-xs">
                                            {it.txn.reference && <div className="font-mono">{it.txn.reference}</div>}
                                            {it.txn.note && <div className="truncate max-w-[220px]">{it.txn.note}</div>}
                                            {!it.txn.reference && !it.txn.note && <span>—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setConfirmDelTxn(it.txn)}
                                                aria-label="Delete transaction"
                                                data-testid={`staff-txn-delete-${it.txn.id}`}
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <RecordStaffTxnDialog
                open={txnOpen}
                onOpenChange={setTxnOpen}
                staff={staff}
                month={month}
                outstanding={summary?.outstanding}
                onSaved={async () => { await load(); onChange?.(); }}
            />

            <PaySalaryDialog
                open={payOpen}
                onOpenChange={setPayOpen}
                staff={staff}
                month={month}
                salaryDue={Math.max(0, Number(summary?.outstanding || summary?.salary_due || 0))}
                onSaved={async () => { await load(); onChange?.(); }}
            />

            <StaffFormDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                initial={staff}
                onSaved={async () => { await load(); onChange?.(); }}
            />

            <AlertDialog open={!!confirmDelTxn} onOpenChange={(v) => !v && setConfirmDelTxn(null)}>
                <AlertDialogContent data-testid="staff-txn-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove this transaction?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the {TYPE_LABEL[confirmDelTxn?.type] || 'transaction'} of{' '}
                            <span className="font-mono">{confirmDelTxn ? fmtMoney(confirmDelTxn.amount) : ''}</span> on {confirmDelTxn ? fmtDateTime(confirmDelTxn.paid_at) : ''}.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); deleteTxn(); }}
                            disabled={busy}
                            data-testid="staff-txn-delete-confirm-btn"
                        >
                            {busy ? 'Removing…' : 'Remove'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={confirmDelStaff} onOpenChange={setConfirmDelStaff}>
                <AlertDialogContent data-testid="staff-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove {staff.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will delete the staff record, all their attendance data, and all their transaction history.
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); deleteStaff(); }}
                            disabled={busy}
                            data-testid="staff-delete-confirm-btn"
                        >
                            {busy ? 'Removing…' : 'Remove staff'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

function BalanceStat({ label, value, className = '', testid }) {
    return (
        <div className="p-4 rounded-lg border border-border bg-card/70">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={`font-heading text-xl mt-1 tabular-nums font-mono ${className}`} data-testid={testid}>
                {value}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------------------
 * Main page — list of staff with monthly summary + inline detail panel
 * ------------------------------------------------------------------------- */
export default function StaffPage() {
    const [rows, setRows] = useState(null);
    const [month, setMonth] = useSharedMonth();
    const [selectedId, setSelectedId] = useState(null);
    const [addOpen, setAddOpen] = useState(false);

    /**
     * Fetch the staff+month roll-up.
     *
     * @param {Object} opts
     * @param {boolean} opts.keepStale
     *   If true, don't blank the current rows while the refetch runs
     *   (i.e. avoid the "table nukes to skeletons" flash after Add/Edit
     *   Save). This gave users the impression that the save had failed.
     *   Default false → fresh page loads still show skeletons.
     */
    async function load({ keepStale = false } = {}) {
        if (!keepStale) setRows(null);
        try {
            const r = await api.listStaff({ month });
            setRows(r || []);
        } catch (e) {
            if (!keepStale) setRows([]);
            toast.error(e?.response?.data?.detail || 'Failed to load staff');
        }
    }

    // Sprint 2026-08: perks companion — perfect-attendance bonus ledger
    // (aggregated by staff for the current calendar year). Small collapsible
    // stats card shown only when at least one bonus has been earned so far,
    // to keep the page uncluttered for fresh tenants.
    const [bonusLedger, setBonusLedger] = useState(null);
    const [bonusOpen, setBonusOpen] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.getStaffBonusLedger();
                if (!cancelled) setBonusLedger(r);
            } catch (e) {
                if (!cancelled) console.debug('[StaffPage] bonus ledger fetch failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, [month]);

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [month]);

    const selectedStaff = useMemo(
        () => (rows || []).find((s) => s.id === selectedId) || null,
        [rows, selectedId],
    );

    return (
        <div className="space-y-5 animate-fade-in">
            <PageHeader
                title="Staff salary"
                subtitle={
                    selectedStaff
                        ? `Attendance, payouts and advances for ${labelForMonth(month)}.`
                        : `Track staff salaries, attendance and payments for ${labelForMonth(month)}. Click a staff member to open their monthly view.`
                }
                actions={
                    <div className="flex items-center gap-2">
                        <MonthPicker value={month} onChange={setMonth} testid="staff-month-picker" />
                        <Button onClick={() => setAddOpen(true)} className="gap-2" data-testid="staff-add-btn">
                            <UserPlus size={16} /> Add staff
                        </Button>
                    </div>
                }
            />

            {selectedStaff && (
                <StaffDetailPanel
                    staff={selectedStaff}
                    month={month}
                    onChange={load}
                    onBack={() => setSelectedId(null)}
                />
            )}

            {!selectedStaff && bonusLedger && Number(bonusLedger.grand_total || 0) > 0 && (
                <Card className="p-4 border-border/80" data-testid="staff-bonus-ledger">
                    <button
                        type="button"
                        onClick={() => setBonusOpen((x) => !x)}
                        className="w-full flex items-center justify-between text-left"
                        data-testid="staff-bonus-ledger-toggle"
                    >
                        <div>
                            <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                                <Coins size={12} className="text-[hsl(var(--price-decrease-fg))]" />
                                Perfect-attendance bonuses paid · {bonusLedger.year}
                            </div>
                            <div className="mt-1 flex items-baseline gap-3">
                                <span className="font-mono text-2xl tabular-nums font-semibold">
                                    {fmtMoney(bonusLedger.grand_total)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    {bonusLedger.eligible_staff_count} of {bonusLedger.total_staff_count} staff earned it
                                </span>
                            </div>
                        </div>
                        <ChevronRight
                            size={16}
                            className={`text-muted-foreground transition-transform ${bonusOpen ? 'rotate-90' : ''}`}
                        />
                    </button>
                    {bonusOpen && (
                        <div className="mt-4 divide-y divide-border" data-testid="staff-bonus-ledger-list">
                            {bonusLedger.entries
                                .filter((e) => e.months_earned > 0)
                                .map((e) => (
                                    <div key={e.staff_id} className="py-2 flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-medium uppercase tracking-wide text-sm truncate">
                                                {e.staff_name}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                                {e.months_earned} month{e.months_earned > 1 ? 's' : ''} ·{' '}
                                                {e.months.map((m) => m.month.slice(5)).join(', ')}
                                            </div>
                                        </div>
                                        <div className="font-mono tabular-nums text-sm shrink-0">
                                            {fmtMoney(e.total_bonus)}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </Card>
            )}

            {!selectedStaff && (
                <Card className="p-0 border-border/80 overflow-hidden">
                    {rows === null ? (
                        <div className="p-4 space-y-2">
                            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-10 text-center">
                            <div className="mx-auto h-10 w-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                                <Users size={20} />
                            </div>
                            <div className="font-heading text-lg mt-3">No staff yet</div>
                            <div className="text-sm text-muted-foreground">
                                Add your first team member to start tracking salaries and advances.
                            </div>
                            <Button onClick={() => setAddOpen(true)} className="mt-4 gap-2" data-testid="staff-empty-add">
                                <UserPlus size={16} /> Add staff
                            </Button>
                        </div>
                    ) : (
                        <>
                        {/* MOBILE CARD VIEW (hidden md+) — Sprint 2026-08: Salary Due
                            was invisible on phones because the desktop table only
                            scrolled horizontally, so it lived off-screen until the
                            user drilled into a staff profile. Now every card shows
                            the salary due prominently on the same row as the name. */}
                        <div className="md:hidden divide-y divide-border">
                            {rows.map((s) => {
                                const out = Number(s.outstanding || 0);
                                const outLabel = out > 0
                                    ? 'You owe'
                                    : out < 0 ? 'They owe' : 'Settled';
                                const outCls = out > 0
                                    ? 'text-[hsl(var(--price-increase-fg))]'
                                    : out < 0
                                        ? 'text-[hsl(var(--price-decrease-fg))]'
                                        : 'text-muted-foreground';
                                // Sprint 2026-08 (Pay Salary): show the quick-pay chip
                                // whenever there's a positive outstanding balance we
                                // could actually settle right now. Uses `outstanding`
                                // (net of advances & prior payments) as the truth
                                // source — matches what the "You owe" label reads.
                                const canPayNow = out > 0;
                                return (
                                    <div
                                        key={s.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedId(s.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSelectedId(s.id);
                                            }
                                        }}
                                        className={`w-full text-left px-4 py-3 hover:bg-muted/40 active:bg-muted/60 transition-colors cursor-pointer ${s.active === false ? 'opacity-60' : ''}`}
                                        data-testid={`staff-mobile-card-${s.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium uppercase tracking-wide truncate">
                                                        {s.name}
                                                    </span>
                                                    {s.active === false && (
                                                        <Badge variant="secondary" className="shrink-0">inactive</Badge>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                                    {(() => {
                                                        // Sprint 2026-08 v2: X/N attendance driven by daily-sheet
                                                        // uploads. N = distinct sheets in month. Fallback to
                                                        // calendar days when no sheets have been uploaded yet.
                                                        const dw = Number(s.days_worked || 0);
                                                        const sheets = Number(s.sheets_in_month || 0);
                                                        const attendance = sheets > 0
                                                            ? `${dw}/${sheets} days present`
                                                            : `${dw}/${s.days_in_month || s.working_days || 30} days`;
                                                        return `Base ${fmtMoney(s.monthly_salary)} · ${attendance}`;
                                                    })()}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                {(() => {
                                                    // Sprint 2026-08: when attendance hasn't been marked for this
                                                    // month yet (days_worked = 0 AND no leave dates recorded), showing
                                                    // "₹0 Salary Due" is misleading — the user reads it as "salary
                                                    // missing/broken." Instead show the monthly base as a projection
                                                    // and prompt them to mark attendance.
                                                    const dw = Number(s.days_worked || 0);
                                                    const monthly = Number(s.monthly_salary || 0);
                                                    const salaryDue = Number(s.salary_due || 0);
                                                    if (dw === 0 && monthly > 0 && salaryDue === 0) {
                                                        return (
                                                            <>
                                                                <div className="font-mono tabular-nums text-base text-muted-foreground">
                                                                    {fmtMoney(monthly)}
                                                                </div>
                                                                <div className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-500">
                                                                    Mark attendance
                                                                </div>
                                                            </>
                                                        );
                                                    }
                                                    return (
                                                        <>
                                                            <div className="font-mono tabular-nums text-base font-semibold">
                                                                {fmtMoney(salaryDue)}
                                                            </div>
                                                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                                                Salary due
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between mt-2 text-[11px] gap-2">
                                            <div className="text-muted-foreground">
                                                Paid {fmtMoney(s.total_settled || 0)}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className={`font-medium ${outCls}`}>
                                                    {outLabel} {fmtMoney(Math.abs(out))}
                                                </div>
                                                {canPayNow && (
                                                    <Button
                                                        size="sm"
                                                        className="h-7 px-2.5 gap-1 text-[11px]"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // Open the drawer AND auto-open the Pay dialog.
                                                            // We stash a lightweight signal on the URL hash
                                                            // — the drawer reads it on mount and pops the
                                                            // dialog, then wipes the hash so refreshes
                                                            // behave normally.
                                                            window.location.hash = `pay-${s.id}`;
                                                            setSelectedId(s.id);
                                                        }}
                                                        data-testid={`staff-mobile-pay-${s.id}`}
                                                        title="Pay salary now"
                                                    >
                                                        <Wallet size={12} /> Pay
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* DESKTOP TABLE VIEW (md+) — unchanged */}
                        <div className="overflow-x-auto hidden md:block">
                            <table className="w-full text-sm" data-testid="staff-table">
                                <thead className="bg-muted/40 text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-4 py-2 font-medium">Name</th>
                                        <th className="text-right px-4 py-2 font-medium">Monthly base</th>
                                        <th className="text-right px-4 py-2 font-medium">Days worked</th>
                                        <th className="text-right px-4 py-2 font-medium">Salary due</th>
                                        <th className="text-right px-4 py-2 font-medium">Paid + advances</th>
                                        <th className="text-right px-4 py-2 font-medium">Outstanding</th>
                                        <th className="px-4 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((s) => {
                                        const out = Number(s.outstanding || 0);
                                        const outCls =
                                            out > 0
                                                ? 'bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))] border border-[hsl(var(--price-increase-border))]'
                                                : out < 0
                                                    ? 'bg-[hsl(var(--price-decrease-bg))] text-[hsl(var(--price-decrease-fg))] border border-[hsl(var(--price-decrease-border))]'
                                                    : 'bg-muted text-muted-foreground border border-border';
                                        return (
                                            <tr
                                                key={s.id}
                                                className={`border-t border-border hover:bg-muted/40 ${s.active === false ? 'opacity-60' : ''}`}
                                            >
                                                <td className="px-4 py-2">
                                                    <button
                                                        onClick={() => setSelectedId(s.id)}
                                                        className="font-medium hover:underline text-left uppercase tracking-wide"
                                                        data-testid={`staff-row-${s.id}`}
                                                    >
                                                        {s.name}
                                                    </button>
                                                    {s.active === false && (
                                                        <Badge variant="secondary" className="ml-2">inactive</Badge>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(s.monthly_salary)}</td>
                                                <td className="px-4 py-2 text-right tabular-nums font-mono text-muted-foreground">
                                                    {(() => {
                                                        // Sprint 2026-08 v2: denominator = sheets uploaded this month.
                                                        const dw = Number(s.days_worked || 0);
                                                        const sheets = Number(s.sheets_in_month || 0);
                                                        return sheets > 0
                                                            ? `${dw}/${sheets}`
                                                            : `${dw}/${s.working_days || 30}`;
                                                    })()}
                                                </td>
                                                <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(s.salary_due || 0)}</td>
                                                <td className="px-4 py-2 text-right tabular-nums font-mono text-muted-foreground">
                                                    {fmtMoney(s.total_settled || 0)}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <span
                                                        className={`inline-block rounded-full px-2 py-0.5 text-xs tabular-nums font-mono ${outCls}`}
                                                        data-testid={`staff-outstanding-${s.id}`}
                                                    >
                                                        {out === 0
                                                            ? 'Settled'
                                                            : out > 0
                                                                ? fmtMoney(out)
                                                                : `-${fmtMoney(Math.abs(out))}`}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        {out > 0 && (
                                                            <Button
                                                                size="sm"
                                                                className="gap-1 h-8"
                                                                onClick={() => {
                                                                    // Deep-link into the drawer's Pay Salary dialog.
                                                                    window.location.hash = `pay-${s.id}`;
                                                                    setSelectedId(s.id);
                                                                }}
                                                                data-testid={`staff-pay-${s.id}`}
                                                                title="Pay salary via UPI or cash"
                                                            >
                                                                <Wallet size={13} /> Pay
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="gap-1"
                                                            onClick={() => setSelectedId(s.id)}
                                                            data-testid={`staff-open-${s.id}`}
                                                        >
                                                            Open <ChevronRight size={14} />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        </>
                    )}
                </Card>
            )}

            <StaffFormDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                initial={null}
                onSaved={() => load({ keepStale: true })}
            />
        </div>
    );
}
